import type {
  BaseNodeData,
  GroupTaskExecutionContext,
  NodeContextPayloadLike,
  NodeContextResource,
} from '@/shared/lib/node-registry'
import {ossThumbnailTiers, resolveOssThumbnailUrl} from "@/shared/lib/oss-url-utils.ts";

export interface BuildNodeGeneratePayloadArgs<TNodeData extends BaseNodeData = BaseNodeData> {
  projectId: string
  nodeId: string
  nodeData: TNodeData
  contextPayloads: NodeContextPayloadLike[]
  groupExecution?: GroupTaskExecutionContext
  resolvedOpData?: Record<string, unknown>
}

export interface TaskInputSourceValue {
  source: 'node'
  nodeId: string
}

export type TaskInputValue<T = unknown> = T | TaskInputSourceValue
export type PromptRefValue = TaskInputValue<string>

export interface PromptInputPayload {
  prompt: string
  prompt_refs: PromptRefValue[]
}

export function readString(value: unknown) {
  return typeof value === 'string' ? value : ''
}

type ContextResourceCarrier = {
  resource?: NodeContextResource
}

/**
 * 读取上下文资源的任务提交 URL。
 *
 * @param item 上游上下文 payload，至少需要包含 resource 字段
 * @returns url 优先，其次 previewUrl；无资源时返回空字符串
 */
export function readContextResourceUrl(item: ContextResourceCarrier | undefined) {
  if (!item?.resource) {
    return ''
  }

  return readString(item.resource.url || item.resource.previewUrl)
}

/**
 * 读取上下文资源的预览 URL。
 *
 * @param item 上游上下文 payload，至少需要包含 resource 字段
 * @returns previewUrl 优先，其次 url；无资源时返回空字符串
 */
export function readContextResourcePreviewUrl(item: ContextResourceCarrier | undefined) {
  if (!item?.resource) {
    return ''
  }

  return readString(item.resource.previewUrl || item.resource.url)
}

/**
 * 从上游上下文 payload 读取缩略图地址。
 */
export function readThumbnailImagePreviewUrl(item: Parameters<typeof readContextResourcePreviewUrl>[0]) {
  const previewUrl = readContextResourcePreviewUrl(item);
  return previewUrl ? resolveOssThumbnailUrl(previewUrl, ossThumbnailTiers[5]) ?? previewUrl : '';
}

/**
 * 读取上下文资源的数字元信息。
 *
 * @param item 上游上下文 payload，至少需要包含 resource 字段
 * @param field 要读取的资源字段名
 * @returns 有效数字返回原值，否则返回 0
 */
export function readContextResourceNumber(
  item: ContextResourceCarrier | undefined,
  field: 'width' | 'height' | 'duration',
) {
  const value = item?.resource?.[field]
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

export function withOptionalContextImageSize<T extends Record<string, unknown>>(
  data: T,
  item: ContextResourceCarrier | undefined,
) {
  const width = readContextResourceNumber(item, 'width')
  const height = readContextResourceNumber(item, 'height')

  return width > 0 && height > 0
    ? { ...data, width, height }
    : data
}

export function readNonEmptyStrings(values: unknown[]) {
  return values
    .map(readString)
    .filter((value) => value.trim().length > 0)
}

export function isDynamicTaskInput(
  item: NodeContextPayloadLike,
  groupExecution?: GroupTaskExecutionContext,
) {
  return groupExecution?.executableNodeIds.has(item.nodeId) === true
}

export function createTaskInputSourceValue(item: NodeContextPayloadLike): TaskInputSourceValue {
  return {
    source: 'node',
    nodeId: item.nodeId,
  }
}

export function isTaskInputSourceValue(value: unknown): value is TaskInputSourceValue {
  return typeof value === 'object'
    && value !== null
    && !Array.isArray(value)
    && (value as { source?: unknown }).source === 'node'
    && typeof (value as { nodeId?: unknown }).nodeId === 'string'
}

export function resolveTaskInputValue<T>(
  item: NodeContextPayloadLike | undefined,
  readStaticValue: (item: NodeContextPayloadLike) => T,
  groupExecution?: GroupTaskExecutionContext,
): TaskInputValue<T> | undefined {
  if (!item) {
    return undefined
  }

  return isDynamicTaskInput(item, groupExecution)
    ? createTaskInputSourceValue(item)
    : readStaticValue(item)
}

export function readContextTaskInputValues<T>(
  items: NodeContextPayloadLike[],
  readStaticValue: (item: NodeContextPayloadLike) => T,
  groupExecution?: GroupTaskExecutionContext,
) {
  return items
    .map((item) => resolveTaskInputValue(item, readStaticValue, groupExecution))
    .filter((value): value is TaskInputValue<T> => {
      if (isTaskInputSourceValue(value)) return true
      if (typeof value === 'string') return value.trim().length > 0
      return value !== undefined && value !== null
    })
}

export function hasPrompt(value: unknown) {
  return readString(value).trim().length > 0
}

export function hasAnyContext(contextPayloads: NodeContextPayloadLike[], kind: string) {
  return contextPayloads.some((item) => item.kind === kind)
}

export function resolvePromptInputPayload({
  groupExecution,
  prompt,
  textContextPayloads,
}: {
  groupExecution?: GroupTaskExecutionContext
  prompt: unknown
  textContextPayloads: NodeContextPayloadLike[]
}): PromptInputPayload {
  return {
    prompt: readString(prompt).trim(),
    prompt_refs: readContextTaskInputValues(
      textContextPayloads,
      (item) => readString(item.data.content),
      groupExecution,
    ),
  }
}

export function hasResolvablePrompt({
  groupExecution,
  prompt: inputPrompt,
  textContextPayloads,
}: {
  groupExecution?: GroupTaskExecutionContext
  prompt: unknown
  textContextPayloads: NodeContextPayloadLike[]
}) {
  const prompt = resolvePromptInputPayload({
    groupExecution,
    prompt: inputPrompt,
    textContextPayloads,
  })

  return prompt.prompt.trim().length > 0 || prompt.prompt_refs.length > 0
}

const GALLERY_GROUP_NODE_ID_PREFIX = 'g$'

export function buildGalleryCompositeNodeId(nodeId: string, placeholderId: string) {
  return `${GALLERY_GROUP_NODE_ID_PREFIX}${nodeId}$${placeholderId}`
}

const TEXT_LIST_GROUP_NODE_ID_PREFIX = 't$'

export function buildTextListCompositeNodeId(nodeId: string, placeholderId: string) {
  return `${TEXT_LIST_GROUP_NODE_ID_PREFIX}${nodeId}$${placeholderId}`
}
